import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import get from "lodash/get";
import includes from "lodash/includes";
import { useState } from "react";
import { Link } from "react-router-dom";
import GoogleMap from "../../../common/js/components/GoogleMaps";
import { Sorty } from "../../../common/js/icons";
import { noPhotoUrl } from "../utils/constants";
import utils, { getFromAttributeValues, markerLabelContent } from "../utils/operations";
import "./Comp.css";
import { NO_CONTENT_PLACEHOLDER } from "./constants";
import schema from "./reviewCompsTables";
import { getCompInfo, getLeaseRate, getSalePrice } from "./utils";

const StyledAlternateContent = styled("div")({
    margin: "auto",
    textAlign: "center",
    marginTop: "6em",
    marginBottom: "6em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
});

const StyledLink = styled(Link)({
    textDecoration: "none",
});

const StyledListingLink = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: "600",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

const StyledWrapper = styled("div")({
    fontFamily: "IBM Plex Sans, sans-serif",
    marginRight: "10px",
    overflowX: "auto",
    flexGrow: 0,
    maxWidth: "41.666667%",
    flexBasis: "41.666667%",
});

const StyledHeaderRow = styled("div")({
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgb(212, 212, 212)",
    color: "#9E9E9E",
    fontSize: "12px",
    fontWeight: "bold",
    padding: "1.5em 0",
    overflow: "hidden",
    scrollbarGutter: "stable",
    flexWrap: "wrap",
    boxSizing: "border-box",
    width: "100%",
});

const StyledBodyRow = styled("div")({
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgb(212, 212, 212)",
    fontSize: "13px",
    height: "fit-content",
    padding: "0.75em 0",
    flexWrap: "wrap",
    boxSizing: "border-box",
});

const StyledBodyWrapper = styled("div")({
    display: "block",
    maxHeight: "50vh",
    minHeight: "425px",
    overflowY: "auto",
    scrollbarGutter: "stable",
});

const CompsWithMap = (props) => {
    const [selectAllLocal, setSelectAllLocal] = useState(true);

    const handleSelectAllLocal = () => {
        const checked = selectAllLocal;
        handleSelectAll(!checked);
        setSelectAllLocal(!checked);
    };

    const {
        compTab,
        handleCheckboxClick,
        handleDragEnd,
        handleSearchCleared,
        handleSelectAll,
        handleSelectAllGroup,
        handleSort,
        isBrokerOnDeal,
        isLoading,
        loadingSearch,
        mapCrumbCleared,
        mapSearchSettings,
        role,
        savedMapCoords,
        saveMapSearchSettings,
        searchCleared,
        searchPolygon,
        searchResponse,
        searchRun,
        selectAll,
        selectAllGroup,
        selectedComps,
        selectedCompsGroup,
        toggleLoadingSearch,
        updateSearchAreaFilter,
        updateSearchPolygon,
    } = props;

    const comps = get(props.comps, compTab) || [];
    const hasComps = Object.keys(comps).length > 0;
    const disableDraw = compTab === "comps_group" ? true : false;
    const headers = schema.viewHeaders["all_km_comps_with_map"];
    const loadRows = !loadingSearch && !searchCleared && hasComps;

    const onDragEnd = (results) => {
        const { destination, source } = results;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;
        handleDragEnd(results, comps, compTab);
    };

    if (compTab === "all_km_comps" && loadingSearch && hasComps) {
        toggleLoadingSearch(false);
    }

    return (
        <div style={{ display: "flex", flexWrap: "wrap", boxSizing: "border-box", width: "100%" }}>
            <StyledWrapper>
                <div style={{ minWidth: "500px" }}>
                    {/* Grid Header */}
                    <StyledHeaderRow>
                        {headers.map((header, i) => {
                            return (
                                <div
                                    key={`gh-c${i}`}
                                    numeric={header.numeric}
                                    onClick={() => handleSort(header.name, compTab)}
                                    style={{
                                        paddingRight: "7px",
                                        flexGrow: 0,
                                        flexBasis:
                                            header.name === "Address"
                                                ? "33.33%"
                                                : header.name === "SQFT"
                                                ? "16.67%"
                                                : "25%",
                                        maxWidth:
                                            header.name === "Address"
                                                ? "33.33%"
                                                : header.name === "SQFT"
                                                ? "16.67%"
                                                : "25%",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {header.name === "Property" ? (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {compTab === "comps_group" ? (
                                                <Checkbox
                                                    className={
                                                        selectAllGroup
                                                            ? "compCheckboxAllSelectedMap"
                                                            : "compCheckboxAllMap"
                                                    }
                                                    checked={selectAllGroup}
                                                    value={selectAllGroup}
                                                    onChange={() => handleSelectAllGroup()}
                                                    color="primary"
                                                />
                                            ) : (
                                                <Checkbox
                                                    className={
                                                        selectAll ? "compCheckboxAllSelectedMap" : "compCheckboxAllMap"
                                                    }
                                                    checked={selectAll}
                                                    value={selectAll}
                                                    onChange={handleSelectAllLocal}
                                                    color="primary"
                                                />
                                            )}
                                            <span style={{ paddingLeft: "7px" }}>{header.name.toUpperCase()}</span>
                                        </div>
                                    ) : (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <span style={{ paddingRight: "7px" }}>{header.name.toUpperCase()}</span>
                                            <Sorty />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </StyledHeaderRow>

                    {/* Grid Body */}
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable" style={{ maxWidth: "100%" }}>
                            {(provided) => (
                                <StyledBodyWrapper {...provided.droppableProps} ref={provided.innerRef}>
                                    {loadRows
                                        ? comps.map((comp, i) =>
                                              row(
                                                  comp,
                                                  i,
                                                  headers,
                                                  compTab,
                                                  role,
                                                  handleCheckboxClick,
                                                  selectedComps,
                                                  selectedCompsGroup,
                                                  isBrokerOnDeal
                                              )
                                          )
                                        : alternateContent(compTab, loadingSearch, hasComps, searchRun)}
                                    {provided.placeholder}
                                </StyledBodyWrapper>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </StyledWrapper>

            <div style={{ flexBasis: "auto", flexGrow: 1, marginTop: "10px", maxWidth: "58.333333%" }}>
                <GoogleMap
                    disableDraw={disableDraw}
                    tab={compTab === "comps_group" ? "group" : "search"}
                    places={compTab === "all_km_comps" ? searchResponse?.results ?? [] : comps ?? []}
                    requestId={searchResponse?.requestId ?? null}
                    savedMapCoords={savedMapCoords}
                    isLoading={isLoading}
                    searchCleared={searchCleared}
                    searchRun={searchRun}
                    markerLabelContentFunc={markerLabelContent}
                    searchByArea={updateSearchAreaFilter}
                    handleSearchCleared={handleSearchCleared}
                    mapCrumbCleared={mapCrumbCleared}
                    searchPolygon={searchPolygon}
                    updateSearchPolygon={updateSearchPolygon}
                    saveMapSearchSettings={saveMapSearchSettings}
                    mapSearchSettings={mapSearchSettings}
                    isListing={false}
                />
            </div>
        </div>
    );
};

const row = (
    comp,
    i,
    headers,
    compTab,
    role,
    handleCheckboxClick,
    selectedComps,
    selectedCompsGroup,
    isBrokerOnDeal
) => {
    const getItemStyle = (isDragging, draggableStyle) => ({
        background: isDragging ? "#C5C5C5" : "",
        userSelect: "none",
        width: "100%",
        ...draggableStyle,
    });

    return (
        <Draggable
            draggableId={`AllCompsWMap Draggable - ${String(i)}`}
            index={i}
            style={{ width: "100%", height: "fit-content" }}
            key={`gb-r${i}`}
        >
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                >
                    <StyledBodyRow>
                        {headers.map((header, j) => {
                            const compPhoto = get(comp, "listing_photo", null)
                                ? get(comp, "listing_photo", null)
                                : get(comp, "property_photo", null)
                                ? get(comp, "property_photo", null)
                                : noPhotoUrl;

                            return (
                                <div
                                    style={{
                                        boxSizing: "border-box",
                                        paddingRight: "7px",
                                        flexGrow: 0,
                                        flexBasis:
                                            header.name === "Address"
                                                ? "33.33%"
                                                : header.name === "SQFT"
                                                ? "16.67%"
                                                : "25%",
                                        maxWidth:
                                            header.name === "Address"
                                                ? "33.33%"
                                                : header.name === "SQFT"
                                                ? "16.67%"
                                                : "25%",
                                    }}
                                    key={`gb-r${i}-c${j}`}
                                    xs={header.name === "Address" ? 4 : header.name === "SQFT" ? 2 : 3}
                                >
                                    {
                                        /* --- Property --- */
                                        header.name === "Property" ? (
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                {compTab === "comps_group" ? (
                                                    <Checkbox
                                                        className={
                                                            selectedCompsGroup.get(comp.dealkey) === true
                                                                ? "compCheckboxAllSelectedMap"
                                                                : "compCheckboxAllMap"
                                                        }
                                                        checked={selectedCompsGroup.get(comp.dealkey) === true}
                                                        value={selectedCompsGroup.get(comp.dealkey) === true}
                                                        onChange={() => handleCheckboxClick(comp.dealkey, compTab)}
                                                        color="primary"
                                                    />
                                                ) : (
                                                    <Checkbox
                                                        className={
                                                            selectedComps.get(comp.dealkey) === true
                                                                ? "compCheckboxAllSelectedMap"
                                                                : "compCheckboxAllMap"
                                                        }
                                                        checked={selectedComps.get(comp.dealkey) === true}
                                                        value={selectedComps.get(comp.dealkey) === true}
                                                        onChange={() => handleCheckboxClick(comp.dealkey, compTab)}
                                                        color="primary"
                                                    />
                                                )}
                                                <div
                                                    className="propertySmallImageContainer"
                                                    style={{
                                                        backgroundImage: `url(${compPhoto})`,
                                                        marginLeft: "7px",
                                                        width: "85px",
                                                        minWidth: "85px",
                                                        height: "85px",
                                                    }}
                                                ></div>
                                            </div>
                                        ) : /* --- Address --- */
                                        header.name === "Address" ? (
                                            <div>
                                                {!getFromAttributeValues(
                                                    comp,
                                                    ["confidentialFlg", "confidential_flg"],
                                                    false
                                                ) ||
                                                includes(["dataEntry", "dataAdmin"], role) ||
                                                isBrokerOnDeal(comp) ? (
                                                    <StyledLink to={`/comp/${comp.dealkey}`} target="_blank">
                                                        <StyledListingLink>
                                                            {/* Using a default value here doesn't work because comp.listing_name will sometimes return Null instead of undefined */}
                                                            {get(comp, "listing_name")
                                                                ? get(comp, "listing_name").trim()
                                                                : ""}
                                                        </StyledListingLink>
                                                    </StyledLink>
                                                ) : (
                                                    <div style={{ color: "red", fontWeight: "bold" }}>Confidential</div>
                                                )}
                                                <div>{`${get(comp, "address1", {})}`}</div>
                                                <div>{`${get(comp, "city", {})}, ${get(comp, "state_code", "")}, ${get(
                                                    comp,
                                                    "zip_postal_code",
                                                    ""
                                                )}`}</div>
                                                <div>{getCompInfo(comp)}</div>
                                            </div>
                                        ) : /* --- Rate / Price --- */
                                        header.name === "Rate/Price" ? (
                                            getFromAttributeValues(
                                                comp,
                                                ["confidentialFlg", "confidential_flg"],
                                                false
                                            ) === true ? (
                                                NO_CONTENT_PLACEHOLDER
                                            ) : (
                                                <div numeric={header.numeric}>
                                                    {
                                                        /* - Lease - */
                                                        utils.parseTransactionType(
                                                            get(comp, "transaction_type", null)
                                                        ) === "Lease"
                                                            ? getLeaseRate(comp)
                                                            : /* - Sale - */
                                                              getSalePrice(comp)
                                                    }
                                                </div>
                                            )
                                        ) : /* --- Square Feet --- */
                                        header.name === "SQFT" ? (
                                            <div numeric={header.numeric}>
                                                {get(comp, header.prop)
                                                    ? header.transform(get(comp, header.prop))
                                                    : NO_CONTENT_PLACEHOLDER}
                                            </div>
                                        ) : null
                                    }
                                </div>
                            );
                        })}
                    </StyledBodyRow>
                </div>
            )}
        </Draggable>
    );
};

const alternateContent = (compTab, loadingSearch, hasComps, searchRun) => {
    return (
        <div>
            {compTab === "all_km_comps" &&
                (loadingSearch ? (
                    <StyledAlternateContent>
                        <CircularProgress color="primary" />
                    </StyledAlternateContent>
                ) : !hasComps ? (
                    searchRun ? (
                        <StyledAlternateContent>No Comps</StyledAlternateContent>
                    ) : (
                        <StyledAlternateContent>Please apply search criteria</StyledAlternateContent>
                    )
                ) : null)}

            {compTab === "comps_group" &&
                (loadingSearch ? (
                    <StyledAlternateContent>
                        <CircularProgress color="primary" />
                    </StyledAlternateContent>
                ) : !hasComps ? (
                    <StyledAlternateContent>No Comps</StyledAlternateContent>
                ) : null)}
        </div>
    );
};

export default CompsWithMap;
